import type { AgAreaSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { capitalize, orderBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { createSearchParams, useNavigate } from 'react-router';

import DonutChart from '@/components/charts/DonutChart';
import type { DonutChartDataTypes } from '@/components/charts/DonutChart/DonutChart.types';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart/HorizontalBarChart';
import StackedAreaChart from '@/components/charts/StackedAreaChart';
import StackedBarChart from '@/components/charts/StackedBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import type { PercentageBarDataType } from '@/components/PercentageBarWidget';
import PercentageBarChart from '@/components/PercentageBarWidget/PercentageBarWidget';
import { QBOperator } from '@/components/QueryBuilder/QueryBuilder.constants';
import { VMP_VULNERABILITY_COLORS } from '@/constants/chartcolors.constants';
import {
  DATE_FORMAT,
  YEAR_MONTH_DAY_TIME_FORMAT
} from '@/constants/date.constants';
import { SEARCH_SEPARATOR_CHAR } from '@/features/SearchBar';
import { ASSET_DATA_PROVIDERS } from '@/pages/Assets/Assets.constants';
import { VMPE_CRITICALITY_PRIORITIES } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.constants';
import {
  useFetchVMPAssets,
  useFetchVulnerabilitiesGroupByOptions
} from '@/pages/VulnerabilityManagement/VulnerabilityManagement.store';
import type { VmpAssetResponseDTO } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.types';
import dashboardTimeFrameAtom, {
  basicVulnerabilitiesSearchParamsAtom,
  dashboardTimeFilterAtom
} from '../Dashboard.store';
import { REMEDIATIONS_STACKED_BAR_SERIES } from '../DashboardTickets/DashboardTickets.constants';
import {
  DASHBOARD_VULNERABILITIES_GROUP_BY_FIELDS,
  STACKED_AREA_SERIES,
  VMPE_BAR_SERIES
} from '../dashboard.constants';
import {
  assetsByOSAtom,
  assetsByScannerTypeAtom,
  assetsByVulnerabilitiesAtom,
  dateFilteredAssetsAtom,
  remediationsPerDayAtom,
  useFetchRemediationMetrics,
  useFetchVulnerabilityMetrics,
  vulnerabilitiesLevelGroupByAtom,
  vulnerabilitiesOverTimeAtom
} from './DashboardVMP.store';
import useStyles from './DashboardVMP.styles';
import VMPSmallCards from './VMPSmallCards/VmpSmallCards';

const DashboardVMP = () => {
  const { isLoading: isAssetsLoading } = useFetchVMPAssets();
  const { isLoading: isVulnMetricsLoading } = useFetchVulnerabilityMetrics();
  const { isLoading: isRemediationsMetricsLoading } =
    useFetchRemediationMetrics();
  const assets = useAtomValue(dateFilteredAssetsAtom);
  const vulnerabilitiesByLevel = useAtomValue(vulnerabilitiesLevelGroupByAtom);
  const vulnerabilitiesOverTime = useAtomValue(vulnerabilitiesOverTimeAtom);
  const assetsByOS = useAtomValue(assetsByOSAtom);
  const assetsByVulnerabilities = useAtomValue(assetsByVulnerabilitiesAtom);
  const remediationsPerDay = useAtomValue(remediationsPerDayAtom);
  const assetsByScannerType = useAtomValue(assetsByScannerTypeAtom);
  const dashboardTimeFrame = useAtomValue(dashboardTimeFrameAtom);
  const timeFilter = useAtomValue(dashboardTimeFilterAtom);

  const basicVulnerabilitiesSearchParams = useAtomValue(
    basicVulnerabilitiesSearchParamsAtom
  );

  const navigate = useNavigate();
  const { classes } = useStyles();

  // Create a separate callback for accessing the latest atom values
  const getLatestTimeValues = useAtomCallback(
    useCallback((get) => {
      const currentTimeFilter = get(dashboardTimeFrameAtom);
      return { currentTimeFilter };
    }, [])
  );

  const searchParams = {
    ...basicVulnerabilitiesSearchParams,
    date_remediated__isnull: true
  };

  const {
    data: vulnerabilitiesGroups,
    isLoading: isVulnerabilitiesGroupsLoading
  } = useFetchVulnerabilitiesGroupByOptions(
    DASHBOARD_VULNERABILITIES_GROUP_BY_FIELDS,
    searchParams
  );

  // Same filters as searchParams, shifted back one full timeframe so the two periods are adjacent and equal length
  const currentPeriodStart = dayjs()
    .subtract(dashboardTimeFrame, 'day')
    .startOf('day');
  const prevPeriodStart = currentPeriodStart.subtract(
    dashboardTimeFrame,
    'day'
  );

  const prevSearchParams = {
    ...basicVulnerabilitiesSearchParams,
    date_identified__gte: prevPeriodStart.format(YEAR_MONTH_DAY_TIME_FORMAT),
    date_identified__lte: currentPeriodStart.format(YEAR_MONTH_DAY_TIME_FORMAT),
    date_remediated__isnull: true
  };

  const { data: prevVulnerabilitiesGroups } =
    useFetchVulnerabilitiesGroupByOptions(
      DASHBOARD_VULNERABILITIES_GROUP_BY_FIELDS,
      prevSearchParams
    );

  // ---- Cvss level graph data
  const vulnerabilitiesByCvssLevelData = useMemo(() => {
    const sortedGroups = orderBy(
      vulnerabilitiesGroups?.cvss_level ?? [],
      [
        (group) =>
          VMPE_CRITICALITY_PRIORITIES[
            group.label as keyof typeof VMPE_CRITICALITY_PRIORITIES
          ] ?? 0
      ],
      ['desc']
    );

    return sortedGroups.map((group) => ({
      name: group.label,
      data: [group.count],
      color:
        VMP_VULNERABILITY_COLORS[
          group.label.toUpperCase() as keyof typeof VMP_VULNERABILITY_COLORS
        ],
      type: 'count'
    }));
  }, [vulnerabilitiesGroups]);
  // --------

  // When bar clicked, navigate to the vmpe page with the appropriate query
  const handleBarClick = useCallback(
    (value: string, type?: string, date?: string) => {
      const { currentTimeFilter } = getLatestTimeValues();

      const operator =
        type === 'remediations' ? QBOperator.IsNotNull : QBOperator.IsNull;
      const nextDay = dayjs(date).add(1, 'day').format(DATE_FORMAT);

      let chartTimeFilter =
        currentTimeFilter === 7
          ? `last-0${currentTimeFilter}:00:00:00|C`
          : `last-${currentTimeFilter}:00:00:00|C`;

      if (type === 'remediations') {
        chartTimeFilter = `custom-${date},${nextDay}|C`;
      }

      navigate({
        pathname: `/vulnerability-management/vulnerabilities`,
        search: createSearchParams({
          t: chartTimeFilter,
          f: JSON.stringify({
            cvss_level: ['__eq', value],
            date_remediated: [operator, true]
          })
        }).toString()
      });
    },
    [timeFilter]
  );

  const handleChartClick = useCallback(
    async (value: string, type: string, filter: string) => {
      const { currentTimeFilter } = await getLatestTimeValues();
      // Determine the chart time filter based on the current time filter
      const chartTimeFilter =
        currentTimeFilter === 7
          ? `last-0${currentTimeFilter}:00:00:00|C`
          : `last-${currentTimeFilter}:00:00:00|C`;

      // Note: setting assets filter like this prevents it from accidentally being set to 7 days on first click
      const assetsTimeFilter =
        currentTimeFilter === 7
          ? `last-0${currentTimeFilter}:00:00:00|U`
          : `last-${currentTimeFilter}:00:00:00|U`;

      if (!value || !type || !filter) {
        return;
      }

      if (type === 'Assets') {
        if (filter === 'operating_system') {
          if (value === 'Other') {
            const otherOS: string[] = [];
            assets.forEach((asset: VmpAssetResponseDTO) => {
              const namedOS = assetsByOS.find(
                (os) => os.name === asset.operating_system
              );
              if (!namedOS && asset.operating_system) {
                otherOS.push(asset.operating_system);
              }
            });
            const newValue = Array.isArray(otherOS)
              ? otherOS.join(SEARCH_SEPARATOR_CHAR)
              : value;

            navigate({
              pathname: '/asset-inventory',
              search: createSearchParams({
                t: assetsTimeFilter,
                q: JSON.stringify({
                  [filter]: newValue,
                  data_provider: ASSET_DATA_PROVIDERS.VMPE
                })
              }).toString()
            });
            return;
          }
          if (value === 'No Operating System') {
            navigate({
              pathname: '/asset-inventory',
              search: createSearchParams({
                t: assetsTimeFilter,
                q: JSON.stringify({
                  [filter]: '',
                  data_provider: ASSET_DATA_PROVIDERS.VMPE
                })
              }).toString()
            });
            return;
          }
        }
        if (filter === 'ip_address') {
          navigate({
            pathname: '/asset-inventory',
            search: createSearchParams({
              t: assetsTimeFilter,
              q: JSON.stringify({
                any: value
              })
            }).toString()
          });
          return;
        }
        navigate({
          pathname: '/asset-inventory',
          search: createSearchParams({
            t: assetsTimeFilter,
            q: JSON.stringify({
              [filter]: value,
              data_provider: ASSET_DATA_PROVIDERS.VMPE.toUpperCase()
            })
          }).toString()
        });
      } else {
        navigate({
          pathname: `/vulnerability-management/${type}`,
          search: createSearchParams({
            t: chartTimeFilter,
            f: JSON.stringify({
              [filter]: ['__eq', value],
              date_remediated: ['__isnull', true]
            })
          }).toString()
        });
      }
    },
    [timeFilter, dashboardTimeFrame, assets]
  );

  const remediationsPerDayChart = useMemo(() => {
    return (
      <GenericChartWidget
        title="Remediations Per Day"
        loading={isRemediationsMetricsLoading}
      >
        <StackedBarChart
          data={remediationsPerDay}
          series={REMEDIATIONS_STACKED_BAR_SERIES}
          handleChartClick={(value: string, date: Date) => {
            handleBarClick(
              value,
              'remediations',
              dayjs(date as Date).format(DATE_FORMAT)
            );
          }}
        />
      </GenericChartWidget>
    );
  }, [dashboardTimeFrame, remediationsPerDay, isRemediationsMetricsLoading]);

  const vulnerabilitiesByLevelChart = useMemo(() => {
    return (
      <GenericChartWidget
        title="Vulnerabilities by CVSS"
        loading={isVulnerabilitiesGroupsLoading}
      >
        <PercentageBarChart
          handleBarClick={(value) => handleBarClick(value)}
          data={vulnerabilitiesByCvssLevelData as PercentageBarDataType[]}
        />
      </GenericChartWidget>
    );
  }, [
    handleBarClick,
    dashboardTimeFrame,
    vulnerabilitiesByLevel,
    isVulnerabilitiesGroupsLoading
  ]);

  const vulnerabilitiesOverTimeChart = useMemo(() => {
    return (
      <GenericChartWidget
        title="Vulnerabilities Over Time"
        loading={isVulnMetricsLoading}
      >
        <StackedAreaChart
          data={vulnerabilitiesOverTime}
          series={STACKED_AREA_SERIES as AgAreaSeriesOptions[]}
          handleChartClick={(value: string) => {
            handleBarClick(capitalize(value));
          }}
        />
      </GenericChartWidget>
    );
  }, [
    dashboardTimeFrame,
    vulnerabilitiesOverTime,
    handleBarClick,
    isVulnMetricsLoading
  ]);

  const assetsByScannerTypeChart = useMemo(() => {
    return (
      <GenericChartWidget
        title="VMPE Assets by Scanner Type"
        loading={isAssetsLoading}
      >
        <DonutChart
          data={assetsByScannerType as DonutChartDataTypes[]}
          resolveCssVariables
          // TODO: implement this when/if we add this information to updated assets page
          onChartClick={(event) => {
            handleChartClick(event.datum.name, 'Assets', 'asset_subtype');
          }}
        />
      </GenericChartWidget>
    );
  }, [dashboardTimeFrame, assetsByScannerType, handleChartClick]);

  const assetsByOSChart = useMemo(() => {
    return (
      <GenericChartWidget
        title="VMPE Assets by Operating System"
        loading={isAssetsLoading}
      >
        <DonutChart
          data={assetsByOS as DonutChartDataTypes[]}
          onChartClick={(event) => {
            handleChartClick(event.datum.name, 'Assets', 'operating_system');
          }}
        />
      </GenericChartWidget>
    );
  }, [dashboardTimeFrame, assetsByOS, handleChartClick]);

  const assetsByVulnerabilitiesChart = useMemo(() => {
    return (
      <GenericChartWidget
        title="Top VMPE Assets by Vulnerabilities"
        loading={isAssetsLoading}
      >
        <HorizontalBarChart
          data={assetsByVulnerabilities}
          series={VMPE_BAR_SERIES}
          handleChartClick={(value: string) =>
            handleChartClick(value, 'Assets', 'ip_address')
          }
        />
      </GenericChartWidget>
    );
  }, [dashboardTimeFrame, assetsByVulnerabilities]);

  return (
    <div className={classes.dashboardPage}>
      <VMPSmallCards
        vulnerabilities={vulnerabilitiesGroups}
        prevVulnerabilities={prevVulnerabilitiesGroups}
        assets={assets}
        timeFilter={timeFilter}
      />
      <div className={classes.dashboardLargeItem}>
        {vulnerabilitiesOverTimeChart}
      </div>
      <div className={classes.dashboardMediumItem}>
        {vulnerabilitiesByLevelChart}
      </div>
      <div className={classes.dashboardMediumItem}>
        {assetsByVulnerabilitiesChart}
      </div>
      <div className={classes.dashboardLargeItem}>
        {remediationsPerDayChart}
      </div>
      <div className={classes.dashboardMediumItem}>{assetsByOSChart}</div>
      <div className={classes.dashboardMediumItem}>
        {assetsByScannerTypeChart}
      </div>
    </div>
  );
};

export default DashboardVMP;
